/**
 * report 命令：报表运维（list / get / rename / delete / patch / create-from-xml）。
 * list：列出租户下报表（递归遍历目录树）。
 *   底层走 /api/packet/loadChildren（parentId + recursive）：服务端部署 recursive 透传后
 *   一次返回完整子树；未部署时自动退化为逐层下钻。patch：读取已有报表 → 局部修改 → 回写，
 *   避免重复创建产生垃圾报表。
 */

import { mkdirSync, readFileSync, writeFileSync } from 'node:fs';
import path from 'node:path';
import { Command } from 'commander';
import { login, resolveTenantOrSelect } from '../client/session.js';
import { requireAuth, resolveAuth, applyConfigDir } from '../core/config.js';
import { getBudgetCharger, withBudget } from '../core/budget.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('cli:report');
import {
  loadReportXml,
  renameReport,
  removeReport,
  createReport,
  updateReportXml,
  publishReport,
  listAllReportFiles,
  listReportFiles,
} from '../client/fileApi.js';
import { designerUrl, viewUrl } from '../client/urls.js';
import { parseReport } from '../core/xml/parse.js';

/**
 * 统一输出：--out 时以 UTF-8 落盘（规避 PowerShell `>` 重定向写 UTF-16 + BOM 的编码坑，
 * 日志实证：report xml > out.xml 在 PowerShell 下写出 UTF-16，node 按 utf8 读全乱码），
 * 并计入预算（通道 B：落盘文本按字数记账）。datasets/ir/xml 均强制 --out，禁止直灌 stdout。
 */
function emitOutput(opts: { out: string }, text: string): void {
  const outPath = path.resolve(opts.out);
  mkdirSync(path.dirname(outPath), { recursive: true });
  writeFileSync(outPath, text, 'utf-8');
  getBudgetCharger().chargeText(text);
  log.info(`已写入: ${outPath}`);
}

export function registerReport(program: Command) {
  const r = program.command('report').description('报表文件运维');

  const auth = (cmd: Command) =>
    cmd
      .option('--base-url <url>', '服务端地址（缺省读 BASK_BASE_URL / .env）')
      .option('--username <user>', '登录用户名（Session 登录；与 --access-token 互斥，缺省读 BASK_USERNAME / .env）')
      .option('--password <pwd>', '登录密码（Session 登录；与 --access-token 互斥，缺省读 BASK_PASSWORD / .env）')
      .option('--access-token <token>', 'OAuth2 访问令牌（与 username/password 互斥；缺省读 BASK_ACCESS_TOKEN / .env）')
      .option('--access-token-header <name>', 'OAuth2 token 注入的 header 名，缺省 authorization（值形如 Bearer <token>）；缺省读 BASK_ACCESS_TOKEN_HEADER / .env')
      .option('--config-dir <dir>', '配置查找目录（含 .env / baskreport.config.json）')
      .option('--tenantId <id>', '租户 ID（可省略；省略时登录后自动解析唯一租户，多租户需显式指定）');

  const loginResolved = async (opts: any, ctx: string) => {
    applyConfigDir(opts);
    const resolved = resolveAuth(opts);
    requireAuth(resolved, ctx);
    const s = await login(resolved);
    // tenantId 未显式提供时，登录后调用 user/get 自动解析；多租户则引导用户（LLM 呈现候选 / TTY 交互）选择。
    s.tenantId = await resolveTenantOrSelect(s, resolved.tenantId);
    return s;
  };

  // 取参考报表数据集（精简视图）：仅输出 name/datasources/datasets（含 SQL 与 fields），
  // 裁剪 cells/rows/columns 等布局/样式/合并信息，供 SOP 4.5 取参考数据集时省 token。
  // 等价于原 `report get --as-json --datasets-only`，拆为独立命令后无需记忆 flag 组合，杜绝漏传致完整 XML 灌入。
  // 每个数据集附带 fromReportId=<报表ID>，声明「该数据集原样取自参考报表 #id，SQL/fields 已由线上报表验证」。
  // LLM 原样复用（不改 SQL/fields、同一数据源）时保留该字段，generate 将跳过该数据集的重复回源校验（省 token）；
  // 改写 SQL 或更换数据源（不同库）时必须移除该字段，表/字段需重新回源确认。
  auth(
    r
      .command('datasets')
      .description('读取参考报表的数据集精简视图（name/datasources/datasets，含 SQL 与 fields，裁剪版式信息）；用于 SOP 4.5 取参考数据集')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--out <file>', '输出写入文件（UTF-8）；强制落盘，禁止直灌标准输出')
      .option('--fields-summary', '字段精简模式：每个数据集只输出 name/type/sql(可选)/fieldsSummary(字段名:类型 单行串)，把数十 KB 的通用 BeanDataset 压到几 KB，先看结构再按需取'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const xml = await loadReportXml(s, Number(opts.id));
    const ir = parseReport(xml);
    if (!ir) {
      throw new Error(
        '该报表根节点不是 <report>（可能是 <dashboard> 仪表盘/图表），不在 IR 承载范围内，无法转 JSON。',
      );
    }
    const datasets = (ir.datasets ?? []).map((d: any) => ({
      ...d,
      fromReportId: Number(opts.id),
    }));
    // 字段精简模式：避免通用 BeanDataset 字段爆炸（日志实证单报表可达 90KB）。
    // 把每个数据集的 fields 压成 "name:type" 单行串，并剔除 rows/示例等大字段。
    if (opts.fieldsSummary) {
      const summary = {
        name: ir.name,
        datasources: ir.datasources ?? [],
        datasets: datasets.map((d: any) => ({
          name: d.name,
          type: d.type,
          ...(d.sql ? { sql: d.sql } : {}),
          fieldsSummary: (d.fields ?? [])
            .map((f: any) => `${f.name}:${f.type ?? '?'}`)
            .join(', '),
        })),
      };
      emitOutput(opts, JSON.stringify(summary, null, 2));
      log.info(
        `字段精简视图已写入 ${opts.out}（${datasets.length} 个数据集；如需完整字段元数据/样例，去掉 --fields-summary 重新拉取）。`,
      );
      return;
    }
    const brief = {
      name: ir.name,
      datasources: ir.datasources ?? [],
      datasets,
    };
    emitOutput(opts, JSON.stringify(brief, null, 2));
  }));;

  // 取完整 IR（JSON 中间层）：含 cells/rows/columns 等完整版式，用于 SOP 9 修改闭环。
  // 等价于原 `report get --as-json`，拆为独立命令。强制 --out：完整 IR 数百 KB，
  // 直灌 stdout 会灌入上下文（坑点 #19 主因），必须落盘后定向读取。
  // --datasets-only：只输出 name/datasources/datasets 框架（剔除 cells/版式），用于「复用参考报表数据集」场景，
  //   把完整 IR（可达 47 万 token）压到几 KB，避免为取 7 个数据集框架而整读大文件（对应 llm.out 实证的最大 token 黑洞）。
  // --in <file>：直接读本地已导出的 IR 文件（不请求服务端），与 --id 二选一，便于对本地大文件做精简提取。
  auth(
    r
      .command('ir')
      .description('读取报表完整 IR（JSON 中间层，含版式/单元格/数据集框架）；--datasets-only 仅取数据集框架；--in 可改读本地 IR 文件')
      .option('--id <id>', '报表文件 ID（与 --in 二选一；服务端取数）')
      .option('--in <file>', '本地 IR 文件路径（与 --id 二选一；不请求服务端，适合对本地大文件做精简提取）')
      .option('--datasets-only', '只输出 name/datasources/datasets 框架（剔除 cells/版式），几 KB 即可拿到数据集定义，避免整读完整 IR')
      .option('--bindings', '仅 --datasets-only 生效：额外从 cells 提取数据集绑定坐标（field → 单元格 row/col/坐标/聚合），一步拿到「字段定义 + 单元格绑定」，免手搓脚本啃原始 XML')
      .requiredOption('--out <file>', '输出写入文件（UTF-8）；强制落盘，禁止直灌标准输出（完整 IR 体积大，直灌将灌入上下文）'),
  ).action(withBudget(async (opts) => {
    if (!opts.id && !opts.in) {
      throw new Error('必须提供 --id <id>（服务端取数）或 --in <file>（本地 IR 文件）二者之一。');
    }
    let ir: any;
    if (opts.in) {
      const raw = readFileSync(opts.in, 'utf-8').trim();
      // 本地 IR 文件可能是已导出的 IR JSON（report ir --out 的产物），也可能是原始 XML。
      // IR JSON 直接解析；XML 需经 parseReport 转 IR。
      if (raw.startsWith('{') || raw.startsWith('[')) {
        ir = JSON.parse(raw);
      } else {
        ir = parseReport(raw);
      }
    } else {
      const s = await loginResolved(opts, 'report 命令');
      const xml = await loadReportXml(s, Number(opts.id));
      ir = parseReport(xml);
    }
    if (!ir) {
      throw new Error(
        '该报表根节点不是 <report>（可能是 <dashboard> 仪表盘/图表），不在 IR 承载范围内，无法转 JSON。',
      );
    }
    // --datasets-only：复用 report datasets 的同款框架结构（含完整 fields，便于复制框架），剔除版式信息。
    if (opts.datasetsOnly) {
      // --bindings：从 cells 提取 dataset 绑定坐标，按数据集聚合为 field → 单元格列表。
      // 用途：LLM 取参考报表时一步拿到「字段定义 + 绑定坐标」，无需 node -e 正则抠 1MB 原始 XML（llm.out 实证的最大 token 黑洞）。
      let bindingsByDataset: Record<string, Record<string, any[]>> | undefined;
      if (opts.bindings) {
        const colName = (col: number): string => {
          let s = '';
          let c = col;
          while (c > 0) {
            const r = (c - 1) % 26;
            s = String.fromCharCode(65 + r) + s;
            c = Math.floor((c - 1) / 26);
          }
          return s;
        };
        bindingsByDataset = {};
        for (const cell of ir.cells ?? []) {
          const c = cell.content;
          if (c && c.kind === 'dataset' && c.dataset) {
            const coord = cell.name ?? `${colName(cell.col)}${cell.row}`;
            const entry = {
              row: cell.row,
              col: cell.col,
              coord,
              ...(c.aggregate ? { aggregate: c.aggregate } : {}),
            };
            const dsMap = (bindingsByDataset[c.dataset] ??= {});
            (dsMap[c.field] ??= []).push(entry);
          }
        }
      }
      const framework = {
        name: ir.name,
        datasources: ir.datasources ?? [],
        datasets: (ir.datasets ?? []).map((d: any) => ({
          name: d.name,
          type: d.type,
          ...(d.sql ? { sql: d.sql } : {}),
          fields: d.fields ?? [],
          ...(opts.bindings
            ? { bindings: bindingsByDataset?.[d.name] ?? {} }
            : {}),
        })),
      };
      emitOutput(opts, JSON.stringify(framework, null, 2));
      const bindingNote = opts.bindings
        ? `（含绑定坐标：每个数据集 fields 之外附 bindings[field] = 单元格列表）`
        : '（不含版式，体积远小于完整 IR）';
      log.info(`数据集框架已写入 ${opts.out}（${framework.datasets.length} 个数据集${bindingNote}）。`);
      return;
    }
    emitOutput(opts, JSON.stringify(ir, null, 2));
  }));;

  // 取原始 XML 源：等价于原 `report get`（无 flag）。强制 --out：原始 XML 体积大，
  // 直灌 stdout 会灌入上下文。
  auth(
    r
      .command('xml')
      .description('读取报表原始 XML 源')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--out <file>', '输出写入文件（UTF-8）；强制落盘，禁止直灌标准输出'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const xml = await loadReportXml(s, Number(opts.id));
    emitOutput(opts, xml);
  }));;

  // 列举报表：从指定父目录递归遍历整棵文件树，收集全部 type==='report' 的报表。
  auth(
    r
      .command('list')
      .description('列出租户下的报表文件（递归遍历目录树）')
      .option('--parent-id <id>', '起始父目录 ID，缺省 0（根目录）；显式指定时只列该目录一层（loadChildren?parentId&recursive=false）')
      .option('--type <type>', '文件类型过滤，缺省 report（仅列报表）', 'report')
      .option('--limit <n>', '最大返回条数，达到即停止递归（缓解报表过多时 token 膨胀）；缺省不限制')
      .option('--json', '仅输出 JSON（缺省额外打印紧凑列表）')
      .option('--out <file>', '将 JSON 输出写入文件（UTF-8）而非打印到标准输出；规避 PowerShell 重定向写 UTF-16 的编码坑，并计入预算'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const parentId = opts.parentId != null ? Number(opts.parentId) : 0;
    const limit = opts.limit != null ? Number(opts.limit) : undefined;
    // --parent-id 显式指定时只列该目录一层；否则递归拉全租户内所有报表。
    const files = await (opts.parentId != null
      ? listReportFiles(s, parentId)
      : listAllReportFiles(s, { parentId, type: opts.type, limit }));
    const reports = opts.parentId != null
      ? files.filter((f: any) => (opts.type ? f.type === opts.type : true))
      : files;
    // 触发 limit 截断时提示用户：结果为子集，应缩小范围（--parent-id / --limit 调大 / --name）再取。
    const truncated = limit != null && reports.length >= limit;
    emitOutput(opts, JSON.stringify(reports, null, 2));
    if (!opts.json) {
      for (const f of reports) {
        const code = f.code ? ` [${f.code}]` : '';
        const designer = designerUrl(s.baseUrl, f.id, s.tenantId);
        console.log(`  ${f.id}\t${f.name}${code}\t${designer}`);
      }
      const typeLabel = opts.type === 'report' ? '报表' : opts.type;
      if (truncated) {
        console.log(
          `\n已截断：仅返回前 ${reports.length} 个${typeLabel}（达到 --limit）。结果不完整，` +
            `请用 --parent-id <目录ID> 缩小范围，或调大 --limit 后重试。`,
        );
      } else {
        console.log(`\n共 ${reports.length} 个${typeLabel}文件。`);
      }
    }
  }));;

  auth(
    r
      .command('rename')
      .description('重命名报表')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--name <name>', '新名称'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    await renameReport(s, Number(opts.id), opts.name);
    console.log('已重命名');
  }));;

  auth(
    r.command('delete').description('删除报表').requiredOption('--id <id>', '报表文件 ID'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    await removeReport(s, Number(opts.id));
    console.log('已删除');
  }));;

  auth(
    r
      .command('patch')
      .description('用新的 XML 文件覆盖更新已有报表（避免重复创建）')
      .requiredOption('--id <id>', '报表文件 ID')
      .requiredOption('--xml <file>', '新 XML 文件'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const id = Number(opts.id);
    // 修改前先发布当前版本：让后台把线上内容封存为可回滚快照，再覆盖新内容。
    // 若该报表从未部署过（无可快照版本），publishReport 返回 false，静默跳过。
    const published = await publishReport(s, id).catch((e) => {
      log.warn(`  [跳过] 修改前发布当前版本失败：${e instanceof Error ? e.message : e}（将继续更新）`);
      return false;
    });
    if (published) console.log('已发布当前版本（用于后续可恢复）：');
    const xml = readFileSync(opts.xml, 'utf-8');
    await updateReportXml(s, id, xml);
    console.log('已更新：');
    console.log(`  设计器: ${designerUrl(s.baseUrl, opts.id, s.tenantId)}`);
    console.log(`  预览:   ${viewUrl(s.baseUrl, opts.id, s.tenantId)}`);
  }));;

  // 便捷：本地 XML 直接创建为报表
  auth(
    r
      .command('create-from-xml')
      .description('由本地 XML 文件创建报表（code 留空，后续以 --id 定位）')
      .requiredOption('--xml <file>', 'XML 文件')
      .option('--name <name>', '报表名称')
      .option('--parent-id <id>', '父目录 ID，缺省 0'),
  ).action(withBudget(async (opts) => {
    const s = await loginResolved(opts, 'report 命令');
    const xml = readFileSync(opts.xml, 'utf-8');
    const file = await createReport(s, {
      parentId: opts.parentId ? Number(opts.parentId) : 0,
      name: opts.name ?? 'AI 生成的报表',
    });
    await updateReportXml(s, file.id, xml);
    console.log(`  设计器: ${designerUrl(s.baseUrl, file.id, s.tenantId)}`);
    console.log(`  预览:   ${viewUrl(s.baseUrl, file.id, s.tenantId)}`);
  }));;
}
